import React, { useCallback, useEffect, useState } from 'react';
import { Tag, Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { adminGetSales, adminSaveSales } from '../lib/api';

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};
const fromLocalInput = (val) => (val ? new Date(val).toISOString() : null);

const fieldCls = 'bg-[#0b0b0b] border border-[#c6a15b]/30 px-3 py-2.5 font-body text-sm text-[#f7f3ec] outline-none focus:border-[#c6a15b]';

const SaleScheduler = ({ flash }) => {
  const [targets, setTargets] = useState({ collections: [], categories: [] });
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminGetSales();
      setTargets(res.targets || { collections: [], categories: [] });
      setRules(res.items || []);
    } catch (e) { flash?.(e.message); }
    setLoading(false);
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const update = (idx, patch) => setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addRule = () => setRules((prev) => [...prev, {
    target_type: 'collection',
    target: targets.collections[0]?.value || '',
    percent: 15,
    starts: null,
    ends: null,
  }]);

  const save = async () => {
    for (const r of rules) {
      if (!r.target) { flash?.('Pick what each sale applies to'); return; }
      const p = Number(r.percent);
      if (!(p > 0 && p <= 90)) { flash?.('Discount must be between 1% and 90%'); return; }
      if (r.starts && r.ends && new Date(r.starts) >= new Date(r.ends)) {
        flash?.('Start must be before end'); return;
      }
    }
    setSaving(true);
    try {
      const payload = rules.map((r) => ({
        target_type: r.target_type,
        target: r.target,
        percent: Number(r.percent),
        starts: r.starts || null,
        ends: r.ends || null,
      }));
      const res = await adminSaveSales(payload);
      setRules(res.items || []);
      flash?.('Sales updated — live across the store');
    } catch (e) { flash?.(e.message); }
    setSaving(false);
  };

  const optionsFor = (type) => (type === 'category' ? targets.categories : targets.collections);

  return (
    <div className="bg-[#141311] border border-[#c6a15b]/20 p-6 mb-10" data-testid="sale-scheduler">
      <div className="flex items-center gap-2 mb-2">
        <Tag size={18} className="text-[#c6a15b]" />
        <h2 className="font-display text-2xl text-[#f7f3ec]">Sale Scheduler</h2>
      </div>
      <p className="font-body text-xs text-[#9c9587] mb-5">
        Put any collection or category on sale by percentage — no code needed. The original price shows struck-through with a green sale price everywhere, and the discount is applied automatically at checkout. Leave dates blank to run indefinitely.
      </p>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 size={22} className="text-[#c6a15b] animate-spin" /></div>
      ) : (
        <>
          <div className="space-y-3">
            {rules.length === 0 && (
              <p className="font-body text-sm text-[#7a7365] italic" data-testid="sale-empty">No active sales. Add one below.</p>
            )}
            {rules.map((r, idx) => (
              <div key={idx} className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr_auto_1.3fr_1.3fr_auto_auto] gap-2 items-center bg-[#0b0b0b]/40 border border-[#c6a15b]/10 p-3" data-testid={`sale-rule-${idx}`}>
                <select
                  value={r.target_type}
                  onChange={(e) => {
                    const type = e.target.value;
                    update(idx, { target_type: type, target: optionsFor(type)[0]?.value || '' });
                  }}
                  data-testid={`sale-type-${idx}`}
                  className={fieldCls}>
                  <option value="collection">Collection</option>
                  <option value="category">Category</option>
                </select>
                <select value={r.target} onChange={(e) => update(idx, { target: e.target.value })}
                  data-testid={`sale-target-${idx}`} className={fieldCls}>
                  {optionsFor(r.target_type).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div className="flex items-center gap-1">
                  <input type="number" min="1" max="90" value={r.percent}
                    onChange={(e) => update(idx, { percent: e.target.value })}
                    data-testid={`sale-percent-${idx}`}
                    className={`${fieldCls} w-20`} />
                  <span className="font-body text-sm text-[#9c9587]">% off</span>
                </div>
                <label className="flex flex-col">
                  <span className="overline text-[0.5rem] text-[#7a7365] mb-1">Starts (optional)</span>
                  <input type="datetime-local" value={toLocalInput(r.starts)}
                    onChange={(e) => update(idx, { starts: fromLocalInput(e.target.value) })}
                    data-testid={`sale-starts-${idx}`} className={fieldCls} />
                </label>
                <label className="flex flex-col">
                  <span className="overline text-[0.5rem] text-[#7a7365] mb-1">Ends (optional)</span>
                  <input type="datetime-local" value={toLocalInput(r.ends)}
                    onChange={(e) => update(idx, { ends: fromLocalInput(e.target.value) })}
                    data-testid={`sale-ends-${idx}`} className={fieldCls} />
                </label>
                <span className={`text-center overline text-[0.5rem] px-2 py-1 border ${r.active ? 'bg-[#1f3a28] text-[#8fe0a6] border-[#4a8c5b]/50' : 'bg-[#2a2620] text-[#9c9587] border-[#c6a15b]/20'}`}
                  data-testid={`sale-active-${idx}`}>
                  {r.active ? 'Live' : 'Scheduled'}
                </span>
                <button onClick={() => setRules((prev) => prev.filter((_, i) => i !== idx))}
                  data-testid={`sale-remove-${idx}`}
                  className="p-2.5 border border-[#8c4a4a]/50 text-[#e0a6a6] hover:bg-[#3a2222] transition-colors justify-self-end">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button onClick={addRule} data-testid="sale-add-btn"
              className="flex items-center gap-2 px-4 py-2.5 border border-[#c6a15b]/40 text-[#e7d3a1] overline text-[0.6rem] hover:bg-white/5 transition-colors">
              <Plus size={14} /> Add Sale
            </button>
            <button onClick={save} disabled={saving} data-testid="sale-save-btn"
              className="btn-gold flex items-center gap-2 px-6 py-2.5 disabled:opacity-60">
              <span className="flex items-center gap-2">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Sales</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SaleScheduler;
